import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate, Navigate, useLocation } from "react-router-dom";
import PrivateRoute from "./routes/PrivateRoute";
import PublicRoute from "./routes/PublicRoute";
import Login from "./pages/Login";
import SignUp from "./pages/SignUp";
import SellerLogin from "./pages/SellerLogin";
import Home from "./pages/Home";
import CustomerRegister from "./pages/CustomerRegister";
import ShopDetails from "./component/ShopDetails";
import CategoryPage from "./pages/CategoryPage";
import StartSelling from "./pages/StartSelling";
import SellerRequestForm from "./pages/SellerRequestForm";
import CustomerLogin from "./pages/CustomerLogin";
import SingleProduct from "./component/ProductSingle"
import ContactPage from "./pages/ContactPage";
import AllProducts from "./pages/AllProducts";
import Deals from "./pages/Deals";
import SearchResults from "./pages/SearchResults";
import Checkout from "./pages/Checkout";
import OrderSuccessPage from "./pages/OrderSuccessPage";
import PendingSeller from "./pages/PendingSeller";
import ForgotPassword from "./pages/ForgotPassword";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import ShippingPolicy from "./pages/ShippingPolicy";
import CategoriesScreen from "./pages/CategoriesScreen";
import AdminLayout from "./admin/adminLayout";
import SellerInvestors from "./admin/pages/SellerInvestors";
import SellerRequests from "./admin/pages/SellerRequests";
import Categories from "./admin/pages/categories";
import SubCategories from "./admin/pages/subCategories";
import Banners from "./admin/pages/banners";
import ReturnControl from "./admin/pages/ReturnControl";
import ProductOptions from "./admin/pages/productOptions";
import ProductTags from "./admin/pages/productTags";
import Shop from "./admin/pages/shop";
import Brands from "./admin/pages/brands";
import ProductFeedback from "./admin/pages/productFeedback";
import ShopRatings from "./admin/pages/ShopRatings";
import ProductInventoryThrestholds from "./admin/pages/productInventoryThrestholds";
import User from "./admin/pages/Users"
import AdminProfile from "./admin/pages/AdminProfile"
import AdminPermissions from "./admin/pages/AdminPermissions";
import AdminReturns from "./admin/pages/AdminReturns";
import AdminCancellations from "./admin/pages/AdminCancellations";
import CouponLayout from "./admin/pages/coupon/CouponLayout";
import CouponDashboard from "./admin/pages/coupon/CouponDashboard";
import CouponList from "./admin/pages/coupon/CouponList";
import CouponForm from "./admin/pages/coupon/CouponForm";
import CouponDetails from "./admin/pages/coupon/CouponDetails";
import CouponUsage from "./admin/pages/coupon/CouponUsage";
import Offers from "./admin/pages/Offers";
import DeliveryAddressSetUp from "./admin/pages/deliveryAddress";
import Deliveries from "./admin/pages/deliveries";
import DeliverySettings from "./admin/pages/deliverySettings";
import DeliveryStaff from "./admin/pages/deliveryStaff";
import DeliveryStaffFee from "./admin/pages/deliveryStaffFee";
import ProductSettings from "./admin/pages/ProductSettings";
import ReferralSettings from "./admin/pages/ReferralSettings";
import SellerPayoutHistory from "./admin/pages/SellerPayoutHistory";
import DeliveryPayoutHistory from "./admin/pages/DeliveryPayoutHistory";
import AdminDeliveryOperations from "./admin/pages/AdminDeliveryOperations";
import LifestyleManager from "./admin/pages/LifestyleManager";
import LifestyleForm from "./admin/pages/LifestyleForm";
import UserLayout from "./seller/sellerLayout";
import ShopSetup from "./seller/page/shopSetUp";
import ShopSetting from "./seller/page/shopSetting"
import ProductsSold from "./seller/page/productsSold";
import ProductOption from "./seller/page/productOption";
import InventoryUpdate from "./seller/page/InventoryUpdate";
import SellerProductFeedback from "./seller/page/SellerProductFeedback";
import OrderIssues from "./seller/page/OrderIssues"
import Profile from "./seller/page/Profile"
import ImportData from "./seller/page/importData";
import ExportData from "./seller/page/exportData";
import ManageLanguage from "./seller/page/manageLanguage";
import ManageCurrency from "./seller/page/manageCurrency";
import DealsManager from "./seller/page/DealsManager";
import SellerEarningsTransfers from "./seller/page/SellerEarningsTransfers";
import ReturnManagement from "./seller/page/ReturnManagement";
import CustomerOrders from "./pages/CustomerOrders";
import MyCredits from "./pages/MyCredits";
import BuyerLayout from "./pages/BuyerLayout";
import BuyerDashboard from "./pages/BuyerDashboard";
import BuyerProfile from "./pages/BuyerProfile";
import BuyerAddresses from "./pages/BuyerAddresses";
import BuyerCancellationRequests from "./pages/BuyerCancellationRequests";
import BuyerReturnRequests from "./pages/BuyerReturnRequests";
import BuyerRewardPoints from "./pages/BuyerRewardPoints";
import BuyerShareEarn from "./pages/BuyerShareEarn";
import BuyerOffers from "./pages/BuyerOffers";
import BuyerSecurity from "./pages/BuyerSecurity";
import NotificationsPage from "./pages/NotificationsPage";
import BuyerWishlist from "./pages/BuyerWishlist";
import PaymentHistory from "./pages/PaymentHistory";
import ChatList from "./component/ChatList";
import MobileBottomNav from "./component/MobileBottomNav";
import CartDrawer from "./component/CartDrawer";
import SessionExpirationModal from "./component/SessionExpirationModal";
import { GlobalAuthModal } from "./util/GlobalAuthModal";
import { bindNotifyInstance } from "./util/Notify";
import { App as AntdApp } from "antd";
import { useCart } from "./CartContext";
import { requestForToken, setupMessageListener } from "./util/notificationUtil";
import io from "socket.io-client";

const AdminDashboard = React.lazy(() => import("./admin/pages/Admindashboard"));
const ProductsCatalog = React.lazy(() => import("./admin/pages/ProductsCatalog"));
const AdminOrders = React.lazy(() => import("./admin/pages/AdminOrders"));
const AddProductPage = React.lazy(() => import("./component/AddProductPage"));
const SellerDashboard = React.lazy(() => import("./seller/page/sellerDashboard"));
const ProductSetUp = React.lazy(() => import("./seller/page/productSetUp"));
const Orders = React.lazy(() => import("./seller/page/orders"));
const SalesReports = React.lazy(() => import("./seller/page/salesReports"));
const ProductReports = React.lazy(() => import("./seller/page/productReports"));
const MonthlyInventory = React.lazy(() => import("./admin/pages/MonthlyInventory"));


// Socket connection will be initialized locally in the App component to prevent memory leaks.
const CartDrawerContent = () => {
  const { isDrawerOpen, setIsDrawerOpen } = useCart();
  return <CartDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />;
};

// Helper component to bind global Notify to Ant Design's App context
const NotifyBinder = () => {
  const { notification } = AntdApp.useApp();
  useEffect(() => {
    bindNotifyInstance(notification);
  }, [notification]);
  return null;
};

const App = () => {
  useEffect(() => {
    requestForToken();
    setupMessageListener((payload) => {
      console.log("Notification received in foreground, refreshing stats:", payload);
      window.dispatchEvent(new Event('notification-refresh'));
    });

    // Managed local socket connection to prevent leaks
    const getSocketUrl = () => {
      const urlStr = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'https://api.nearzon.com';
      try {
        return new URL(urlStr).origin; // safely returns "https://api.nearzon.com" without corrupting subdomains
      } catch (e) {
        return 'https://api.nearzon.com';
      }
    };
    
    const socket = io(getSocketUrl(), {
      autoConnect: true,
    });

    // Global listener for Shop Availability updates
    socket.on('shopStatusUpdated', (data) => {
      console.log("🏪 Global Shop Status Updated:", data);
      window.dispatchEvent(new CustomEvent('shopStatusUpdated', { detail: data }));
    });

    return () => {
      socket.off('shopStatusUpdated');
      socket.disconnect();
    };
  }, []);

  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Listen for session expiration event
    const handleSessionExpired = () => {
      setShowSessionExpired(true);
    };

    window.addEventListener("session-expired", handleSessionExpired);
    return () => window.removeEventListener("session-expired", handleSessionExpired);
  }, []);

  const handleSessionExpiredConfirm = () => {
    setShowSessionExpired(false);
    navigate("/login");
  };

  return (
    <>
      <NotifyBinder />
      <GlobalAuthModal>
        <SessionExpirationModal
          isOpen={showSessionExpired}
          onConfirm={handleSessionExpiredConfirm}
        />
        <React.Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><div className="loader">Loading...</div></div>}>
          <Routes>
            {/* ... routes ... */}



            {/* 🌐 Public Routes - No Auth Required */}
            <Route path="/" element={<Home />} />
            {/* Keep Shops valid if needed, or redirect. For now Home replaces Shops */}
            <Route path="/products/:shopId" element={<ShopDetails />} />
            <Route path="/category/:categoryName" element={<CategoryPage />} />
            <Route path="/single/Products/:id" element={<SingleProduct />} />
            <Route path="/start-selling" element={<StartSelling />} />
            <Route path="/seller-check" element={<SellerLogin />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/all-products" element={<AllProducts />} />
            <Route path="/deals" element={<Deals />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/shipping-policy" element={<ShippingPolicy />} />

            <Route path="/search" element={<SearchResults />} />
            <Route path="/checkout" element={<PrivateRoute allowedRoles={["user"]}><Checkout /></PrivateRoute>} />
            <Route path="/order-success" element={<PrivateRoute allowedRoles={["user"]}><OrderSuccessPage /></PrivateRoute>} />
            <Route path="/categories" element={<CategoriesScreen />} />

            {/* 🚪 Auth Routes - Restricted if already logged in */}
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/customer-login" element={<PublicRoute><CustomerLogin /></PublicRoute>} />
            <Route path="/sellerlogin" element={<PublicRoute><SellerLogin /></PublicRoute>} />
            <Route path="/signup" element={<PublicRoute><SignUp /></PublicRoute>} />
            <Route path="/sellersignup" element={<PublicRoute><SellerLogin /></PublicRoute>} />
            <Route path="/customer-register" element={<PublicRoute><CustomerRegister /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />

            {/* 🛒 Buyer Routes - Role: user */}
            <Route path="/buyer" element={<PrivateRoute allowedRoles={["user"]}><BuyerLayout /></PrivateRoute>}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<BuyerDashboard />} />
              <Route path="orders" element={<CustomerOrders />} />
              <Route path="profile" element={<BuyerProfile />} />
              <Route path="addresses" element={<BuyerAddresses />} />
              <Route path="cancellation-requests" element={<BuyerCancellationRequests />} />
              <Route path="return-requests" element={<BuyerReturnRequests />} />
              <Route path="rewards/points" element={<BuyerRewardPoints />} />
              <Route path="rewards/share" element={<BuyerShareEarn />} />
              <Route path="rewards/offers" element={<BuyerOffers />} />
              <Route path="security" element={<BuyerSecurity />} />
              <Route path="notifications" element={<NotificationsPage role="user" />} />
              <Route path="wishlist" element={<BuyerWishlist />} />
              <Route path="rewards/credits" element={<MyCredits />} />
              <Route path="payments/history" element={<PaymentHistory />} />
              <Route path="contact" element={<ContactPage />} />
              <Route path="messages" element={<ChatList />} />
            </Route>

            {/* 🏢 Seller Routes - Role: seller */}
            <Route path="/seller" element={<PrivateRoute allowedRoles={["seller"]}><UserLayout /></PrivateRoute>}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<SellerDashboard />} />
              <Route path="product-setup" element={<ProductSetUp />} />
              <Route path="product-setup/add" element={<AddProductPage />} />
              <Route path="product-setup/edit/:id" element={<AddProductPage />} />
              <Route path="shop-setup" element={<ShopSetup />} />
              <Route path="shop-settings" element={<ShopSetting />} />
              <Route path="product-option" element={<ProductOption />} />
              <Route path="product-sold" element={<ProductsSold />} />
              <Route path="inventory-update" element={<InventoryUpdate />} />
              <Route path="seller-product-feedback" element={<SellerProductFeedback />} />
              <Route path="profile" element={<Profile />} />
              <Route path="orders" element={<Orders />} />
              <Route path="order-issues" element={<OrderIssues />} />
              <Route path="earnings" element={<SellerEarningsTransfers />} />
              <Route path="returns" element={<ReturnManagement />} />
              <Route path="import" element={<ImportData />} />
              <Route path="export" element={<ExportData />} />
              <Route path="reports/sales" element={<SalesReports />} />
              <Route path="reports/products" element={<ProductReports />} />
              <Route path="deals" element={<DealsManager />} />
              <Route path="notifications" element={<NotificationsPage role="seller" />} />
              <Route path="messages" element={<ChatList />} />
            </Route>

            {/* 🛡️ Admin Routes - Role: admin, superadmin */}
            <Route path="/admin" element={<PrivateRoute allowedRoles={["admin", "superadmin"]}><AdminLayout /></PrivateRoute>}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="products-catalog" element={<ProductsCatalog />} />
              <Route path="monthly-inventory" element={<MonthlyInventory />} />
              <Route path="products-catalog/add" element={<AddProductPage />} />
              <Route path="products-catalog/edit/:id" element={<AddProductPage />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="seller-investors" element={<SellerInvestors />} />
              <Route path="seller-requests" element={<SellerRequests />} />
              <Route path="categories" element={<Categories />} />
              <Route path="sub-categories" element={<SubCategories />} />
              <Route path="lifestyles" element={<LifestyleManager />} />
              <Route path="lifestyles/add" element={<LifestyleForm />} />
              <Route path="lifestyles/edit/:id" element={<LifestyleForm />} />

              <Route path="product-options" element={<ProductOptions />} />
              <Route path="product-tags" element={<ProductTags />} />
              <Route path="shop" element={<Shop />} />
              <Route path="shop/:id" element={<Shop />} />
              <Route path="shop-ratings" element={<ShopRatings />} />
              <Route path="brands" element={<Brands />} />
              <Route path="user" element={<User />} />
              <Route path="product-feedback" element={<ProductFeedback />} />
              <Route path="product-settings" element={<ProductSettings />} />
              <Route path="product-inventory-thresholds" element={<ProductInventoryThrestholds />} />
              <Route path="profile" element={<AdminProfile />} />
              <Route path="admin-permissions" element={<AdminPermissions />} />
              <Route path="returns" element={<AdminReturns />} />
              <Route path="cancellations" element={<AdminCancellations />} />
              <Route path="return-rules" element={<ReturnControl />} />

              {/* 🎟️ Coupon Management Module */}
              <Route path="coupons" element={<CouponLayout />}>
                <Route index element={<Navigate to="list" replace />} />
                <Route path="dashboard" element={<CouponDashboard />} />
                <Route path="list" element={<CouponList />} />
                <Route path="create" element={<CouponForm />} />
                <Route path="edit/:id" element={<CouponForm />} />
                <Route path="details/:id" element={<CouponDetails />} />
                <Route path="usage" element={<CouponUsage />} />
              </Route>

              <Route path="offers" element={<Offers />} />
              <Route path="banners" element={<Banners />} />
              <Route path="notifications" element={<NotificationsPage role="admin" />} />
              {/* 🚚 Admin Delivery Management */}
              <Route path="deliveries" element={<Deliveries />} />
              <Route path="delivery-ops" element={<AdminDeliveryOperations />} />
              <Route path="delivery-settings" element={<DeliverySettings />} />
              <Route path="delivery-staff" element={<DeliveryStaff />} />
              <Route path="delivery-staff-fee" element={<DeliveryStaffFee />} />
              <Route path="delivery-address" element={<DeliveryAddressSetUp />} />
              <Route path="messages" element={<ChatList />} />
              <Route path="referral-settings" element={<ReferralSettings />} />
              <Route path="seller-payouts" element={<SellerPayoutHistory />} />
              <Route path="delivery-payouts" element={<DeliveryPayoutHistory />} />
              <Route path="import" element={<ImportData />} />
              <Route path="export" element={<ExportData />} />
              <Route path="language" element={<ManageLanguage />} />
              <Route path="currency" element={<ManageCurrency />} />
            </Route>

            {/* 📝 Misc & Fallbacks */}
            <Route path="/seller-request-form" element={<PrivateRoute allowedRoles={["user"]}><SellerRequestForm /></PrivateRoute>} />
            <Route path="/pending-seller" element={<PrivateRoute allowedRoles={["pendingseller"]}><PendingSeller /></PrivateRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </React.Suspense>
        {!['/checkout', '/order-success'].includes(location.pathname) && <MobileBottomNav />}
        <CartDrawerContent />
      </GlobalAuthModal>
    </>
  );
};

export default App;
